import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { StudyHelperInfo } from '@/tables/StudyHelperInfo';
import {
  type StudyHelperHistoryRecord,
  StudyHelpersHistoryTable,
} from '@/tables/StudyHelpersHistoryTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { apiLanguageToLocale } from '~/utils/languageFormat';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/study-helpers',
)({
  component: MemberStudyHelpersRoute,
});

export default function MemberStudyHelpersRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);

  // Legacy refuses this tab by never registering it in the nav, so a denied
  // viewer gets an HTTP 404 page rather than a permission message
  // (`themes/wbs/modules/global-classes/WBS_Profile_Extension.php:19-23`).
  // `viewer-access` answers 200 for every authenticated caller, so one query
  // decides the whole page without reading a status code, and it gates on the
  // same audience the two study-helper operations do.
  //
  // No `retry: false` here on purpose: the gate below is positive, so a retried
  // transient failure holds the page at `null` and can never leak content.
  const { data: access, isLoading } = api.getStudentViewerAccess.useQuery({
    path: { student_id: studentId },
  });
  const { data: history } = api.getStudentStudyHelperHistory.useQuery({
    path: { student_id: studentId },
  });

  if (isLoading) {
    // Not a no-op: rendering the headings before `access` resolves would flash
    // them at a viewer the next line refuses.
    return null;
  }

  // Positively gated. `access` is `undefined` on a 401, a 500 or a dropped
  // connection, and every one of those is a refusal — a `can_view === false`
  // test would fall through on all three and render the page, whose own failing
  // queries then tell the viewer the student has no study helper.
  if (access?.can_view !== true) {
    return <NotFoundScreen />;
  }

  // The caption comes from `viewer-access`, not from `getStudent`: that
  // operation is admin-or-self and carries 17 fields, which is a lot of student
  // to hand a supervisory viewer in exchange for a table title.
  const studentName = access.display_name ?? '';
  const historyRecords: StudyHelperHistoryRecord[] = (history ?? []).map(
    (row) => ({
      id: String(row.id),
      studyHelper: row.study_helper_name,
      location: row.location,
      languages: row.languages.map(apiLanguageToLocale),
      adoptedAt: row.adopted_at,
      releasedAt: row.released_at,
      releasedBy: row.released_by,
    }),
  );

  return (
    <MainContent>
      {/* Both headings and the history table render even when no helper is
          assigned; only the helper's details and the Message button sit inside
          legacy's `if ($study_helper_id)` branch
          (`buddypress/members/single/study-helpers/overview.php:4,70,81`). */}
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.current-study-helper')} />
      </GroupHeaderWrapper>
      <StudyHelperInfo studentId={studentId} />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.study-helper-history')} />
      </GroupHeaderWrapper>
      <StudyHelpersHistoryTable
        studentName={studentName}
        data={historyRecords}
      />
    </MainContent>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  margin: 34px 0 30px 0;
  min-width: 0;
`;
